import { API_ORIGIN } from '@/api/config';

/**
 * Cross-tab auth signaling. One BroadcastChannel carries two transitions:
 * a tab that verified a session announces it, a tab that signed out announces
 * it. Receivers apply state or re-probe; they never re-broadcast, so a burst
 * of tabs cannot loop.
 */

export type AuthSignal = { kind: 'authenticated' } | { kind: 'signed-out' };

const NAME = 'poseidon-auth';

function channel(): BroadcastChannel | null {
  try {
    return new BroadcastChannel(NAME);
  } catch {
    return null; // very old engines: tabs fall back to per-tab state
  }
}

let publisher: BroadcastChannel | null | undefined;

export function publishAuthSignal(signal: AuthSignal): void {
  // One long-lived publisher per tab: closing immediately after postMessage
  // can drop the queued delivery.
  if (publisher === undefined) publisher = channel();
  publisher?.postMessage(signal);
}

export function subscribeAuthSignals(onSignal: (s: AuthSignal) => void): () => void {
  const c = channel();
  if (!c) return () => {};
  const handler = (e: MessageEvent) => {
    const d = e.data as AuthSignal;
    if (d && (d.kind === 'authenticated' || d.kind === 'signed-out')) onSignal(d);
  };
  c.addEventListener('message', handler);
  return () => {
    c.removeEventListener('message', handler);
    c.close();
  };
}

/**
 * Clear the Google IAP session cookie at the API origin without leaving the
 * page: IAP's documented CLEAR_LOGIN_COOKIE mode on any protected URL. The
 * response is opaque (no-cors); the browser still processes its Set-Cookie.
 *
 * Awaitable, because the one caller that matters navigates to IAP straight
 * after: if the clear has not landed, IAP re-admits the same refused account
 * and the operator arrives back on the identical screen with no explanation.
 * It used to guess at 400ms. A failure still resolves, since a sign-out must
 * not be blocked by a request that will not complete.
 */
export async function clearIapSession(): Promise<void> {
  if (!API_ORIGIN.startsWith('https://')) return; // dev bypass has no IAP cookie
  try {
    await fetch(`${API_ORIGIN}/healthz?gcp-iap-mode=CLEAR_LOGIN_COOKIE`, {
      mode: 'no-cors',
      credentials: 'include',
      cache: 'no-store',
    });
  } catch {
    // The cookie may or may not be gone; proceeding is still the right move.
  }
}
